/**
 * This is a function to unwrap the current theming implementation to hide it from the public API
 * until we're ready to expose it in a unified manner.
 */

import { ThemeKey } from "@smartsheet/design-tokens";
import { isValidElement } from "react";

/**
 * The named themes that lodestar-core supports.
 *
 * We've added a dark theme here that DOES NOTHING YET!
 *
 * You're not allowed to add the 'dark' key to a ThemeSwitch in lodestar-core,
 * and any usages in non-ts code will fall back to 'light' for that value.
 * Since most of this code isn't exposed, we don't really have to worry about
 * that case.
 *
 * For more info, please see:
 * https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/396
 *
 */
export type ThemeMode =
    // "light" is an alias for "lodestar-light". Remains for back-compat.
    | "light"
    // This is the legacy app-core styling.
    | "oldestar"
    // This is a key we added for proofing to add some custom dark-mode components to their dark xp.
    // See: https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/396
    | "dark"
    | ThemeKey;

const themeAliasMap = {
    oldestar: "oldestar",
    light: "lodestar-light",
    dark: "lodestar-light",
    "lodestar-light": "lodestar-light",
    "ursa-light": "ursa-light",
    // this works for now since all dark theme values are set at the provider level
    // we want ursa-dark to return the same values as ursa-light via theme objects
    "ursa-dark": "ursa-dark",
} as const; // TODO ts 4.9 satisfies { [aliasName in ThemeMode]: ThemeMode };

/**
 * For some of these keys, we don't use them. Some are aliases for now, some will be aliases until
 * we can remove them. The union of the values of this object are the themes that actually have
 * values for them. That union is used to enforce the themed functions argument's leaf nodes all
 * have values assigned for each non-alias theme.
 *
 * For example: "light" is an alias of "lodestar-light" for back-compat.
 */
export const resolveThemeAlias = (modeOrAlias: keyof typeof themeAliasMap): RequiredThemeKeys =>
    themeAliasMap[modeOrAlias];

/**
 * These are the theme name keys required in the leaf nodes of any object passed to `themed()`.
 */
export type RequiredThemeKeys = (typeof themeAliasMap)[keyof typeof themeAliasMap];

// Theme values can be strings (font color), numbers (font weight), booleans, or react nodes (icons).
export type ThemeValueTypes = string | number | boolean | React.ReactNode;

// This is a leaf node in a theme object.
// It is the switch that should contain the different values to use for a given theme.
export type ThemeSwitch<R extends ThemeValueTypes = ThemeValueTypes> = {
    [key in RequiredThemeKeys]: R;
};

export type RequiredKeysForThemeSwitch<T extends ThemeValueTypes = ThemeValueTypes> = Omit<
    ThemeSwitch<T>,
    // omitted so they aren't required in the theme objects — fallbacks are set up below
    "oldestar" | "ursa-dark"
>;

// Used to validate a ThemeSwitch at runtime. We just need to ensure we create a mapping of all the
// possible theme names to extract into an array that will break if we add another theme.
const basicThemeSwitchForAllThemesInference: RequiredKeysForThemeSwitch<string> = {
    "ursa-light": "these",
    "lodestar-light": "aren't used",
} as const;

const requiredThemes = Object.keys(basicThemeSwitchForAllThemesInference);
const isThemeSwitch = (t: unknown): t is ThemeSwitch<ThemeValueTypes> => {
    return (
        !!t &&
        typeof t === "object" &&
        requiredThemes.every((themeName) => Object.prototype.hasOwnProperty.call(t, themeName))
    );
};

// Each property in a theme object can be mapped to a switch, value, or another theme object.
export type ThemeNode = {
    [key: string]: ThemeValueTypes | DeepThemeObject;
};

// This is the type of the root of a theme object. It's either a deep theme object or a switch.
// Having just a value passed to themed would be silly, and makes the typings more complicated.
export type DeepThemeObject = ThemeSwitch | ThemeNode;

// Ok here's the big ol' mapped type. This is the thing that will map the type of the theme object
// you pass to themed to the thing that will be returned. It makes sure that the shape is mostly the same,
// but adds in ThemeSwitch types at each leaf of the returned theme tree.
export type ThemeResolver<R extends ThemeValueTypes> = (mode: ThemeMode) => R;

export type DeepThemeResolver<T extends DeepThemeObject> =
    // If it is a ThemeSwitch, convert to a ThemeResolver
    T extends RequiredKeysForThemeSwitch<infer SwitchType>
        ? ThemeResolver<SwitchType>
        : // Disallow partial ThemeSwitch
        T extends Partial<RequiredKeysForThemeSwitch<never>>
        ? `themed() error: You must provide all theme keys. Missing: ${Exclude<keyof ThemeSwitch, keyof T>}`
        : // Otherwise it's a deep object
          {
              [P in keyof T]: T[P] extends string
                  ? ThemeResolver<string>
                  : T[P] extends number
                  ? ThemeResolver<number>
                  : T[P] extends ThemeSwitch<infer SwitchType>
                  ? ThemeResolver<SwitchType>
                  : T[P] extends ThemeNode
                  ? DeepThemeResolver<T[P]>
                  : never;
          };

/**
 * Used to create a theme from an object arbitrary shape which will be mapped
 * into an object of similar shape with the leaf nodes replaced.
 *
 * Ex:
 * ```
 * const theme = themed({
 *   keyA: {
 *     "lodestar-light": "lodestarLightValue",
 *     oldestar: "optionalOldestarValue" // this line is not required.
 *     "ursa-light": "ursaValue",
 *   }
 * });
 * ```
 *
 * `theme` will now be of the shape:
 * ```
 * {
 *   keyA: (themeName: keyof ThemeMode) => string; // Where that string is "lightValue" | "ursaValue".
 * }
 * ```
 *
 * And you would get a themed value by passing in the theme to the right key function:
 *
 * ```
 * const keyAValue = theme.keyA("lodestar-light"); // => returns "lightValue"
 * ```

 * @param obj A DeepThemeObject that will be converted to a ThemeResolver.
 */
export const themed = <T extends DeepThemeObject>(obj: T): DeepThemeResolver<T> => {
    function helper(inner: DeepThemeObject | ThemeValueTypes) {
        if (
            typeof inner === "string" ||
            typeof inner === "number" ||
            typeof inner === "boolean" ||
            isValidElement(inner)
        ) {
            return () => inner;
        } else if (isThemeSwitch(inner)) {
            // We allow all modes to be passed, then those modes are mapped if needed using the
            // rules above in themeAliasMap.
            return (mode: ThemeMode) => {
                // We don't require oldestar values and they fall back to lodestar-light, but we DO
                // want to allow them to be provided if required.
                if (mode === "oldestar" && !("oldestar" in inner)) {
                    return inner["lodestar-light"];
                }
                if (mode === "ursa-dark" && !("ursa-dark" in inner)) {
                    return inner["ursa-light"];
                }
                return inner[themeAliasMap[mode]];
            };
        } else if (inner && typeof inner === "object") {
            const newObj: any = {};
            Object.keys(inner).forEach((key) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                newObj[key] = helper(inner[key as keyof typeof inner]);
            });
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return newObj;
        } else {
            return null; // or handle other cases appropriately
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return helper(obj);
};
